import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/jf/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Confirmed", "In Production", "Delivered", "Cancelled"];

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("jf_admin_token") || "");
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const load = () =>
    axios.get(`${API}/orders`, { headers })
      .then((r) => setOrders(r.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))))
      .catch(() => {
        localStorage.removeItem("jf_admin_token");
        setToken("");
      });

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await axios.post(`${API}/auth/login`, creds);
      localStorage.setItem("jf_admin_token", r.data.token);
      setToken(r.data.token);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    }
  };

  const logout = () => {
    localStorage.removeItem("jf_admin_token");
    setToken("");
    setOrders([]);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/orders/${id}`, { status }, { headers });
      toast.success(`Marked as ${status}`);
      load();
    } catch {
      toast.error("Could not update status.");
    }
  };

  if (!token) {
    return (
      <main data-testid="admin-login" className="bg-oxblood min-h-screen flex items-center justify-center px-6">
        <Reveal className="w-full max-w-md">
          <form onSubmit={login} className="bg-ivory border border-brass/40 p-10 md:p-12">
            <img src="/logo.png" alt="Jaipur Furniture" className="h-16 w-auto mb-8" />
            <p className="text-oxblood text-xs tracking-[0.4em] uppercase mb-2">Admin</p>
            <h1 className="font-display text-oxblood-ink text-3xl mb-8">Orders Dashboard</h1>
            {error ? <p data-testid="admin-login-error" className="mb-6 text-oxblood text-sm font-light border border-oxblood/30 bg-oxblood/5 px-4 py-3">{error}</p> : null}
            <div className="space-y-6">
              <input
                data-testid="admin-email-input"
                type="email"
                value={creds.email}
                onChange={(e) => setCreds({ ...creds, email: e.target.value })}
                placeholder="Email"
                className="w-full bg-transparent border-b border-oxblood/30 focus:border-brass py-4 text-ink placeholder:text-ink/35 font-light"
              />
              <input
                data-testid="admin-password-input"
                type="password"
                value={creds.password}
                onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                placeholder="Password"
                className="w-full bg-transparent border-b border-oxblood/30 focus:border-brass py-4 text-ink placeholder:text-ink/35 font-light"
              />
            </div>
            <button
              data-testid="admin-login-btn"
              type="submit"
              className="mt-10 w-full bg-oxblood text-ivory hover:bg-oxblood-ink py-4 text-xs tracking-[0.3em] uppercase transition-colors duration-400"
            >
              Sign In
            </button>
          </form>
        </Reveal>
      </main>
    );
  }

  const counts = STATUSES.map((s) => [s, orders.filter((o) => (o.status || "New") === s).length]);

  return (
    <main data-testid="admin-dashboard" className="bg-ivory min-h-screen pt-32 md:pt-40 pb-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-oxblood text-xs tracking-[0.45em] uppercase mb-4">Admin</p>
            <h1 className="font-display text-oxblood-ink text-4xl md:text-6xl">Bookings <em className="text-oxblood font-light">received.</em></h1>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="border border-oxblood/40 text-oxblood hover:bg-oxblood hover:text-ivory text-xs tracking-[0.25em] uppercase px-6 py-3 transition-colors duration-300"
          >
            Sign Out
          </button>
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <div data-testid="stat-total" className="border border-oxblood/25 px-6 py-4">
            <span className="font-display text-oxblood-ink text-2xl">{orders.length}</span>
            <span className="block text-oxblood/60 text-[10px] tracking-[0.3em] uppercase mt-1">Total</span>
          </div>
          {counts.map(([s, n]) => (
            <div key={s} data-testid={`stat-${s.toLowerCase().replace(/\s+/g, "-")}`} className="border border-oxblood/15 px-6 py-4">
              <span className="font-display text-oxblood-ink text-2xl">{n}</span>
              <span className="block text-oxblood/60 text-[10px] tracking-[0.3em] uppercase mt-1">{s}</span>
            </div>
          ))}
        </div>

        <div className="mt-12 space-y-4">
          {orders.length === 0 ? (
            <p className="text-ink/50 font-light">No bookings yet.</p>
          ) : (
            orders.map((o, i) => (
              <Reveal key={o.id} delay={Math.min(i * 0.03, 0.3)}>
                <div data-testid={`order-row-${i}`} className="bg-cream/60 border border-oxblood/15 p-6 md:p-8 grid md:grid-cols-12 gap-4 md:gap-6 items-start">
                  <div className="md:col-span-3">
                    <p className="font-display text-oxblood-ink text-xl">{o.name}</p>
                    <p className="text-ink/55 font-light text-sm mt-1">{o.phone}</p>
                    {o.city ? <p className="text-ink/45 font-light text-sm">{o.city}</p> : null}
                    {o.advance_paid ? (
                      <span className="inline-block mt-2 bg-brass/20 border border-brass text-oxblood text-[10px] tracking-[0.25em] uppercase px-3 py-1">
                        Advance Paid
                      </span>
                    ) : null}
                  </div>
                  <div className="md:col-span-3">
                    <span className="text-oxblood/60 text-[10px] tracking-[0.3em] uppercase">Space</span>
                    <p className="font-display text-oxblood-ink text-lg mt-1">{o.space_type || "—"}</p>
                    {o.product_name ? <p className="text-ink/55 font-light text-sm mt-1 italic">{o.product_name}</p> : null}
                  </div>
                  <div className="md:col-span-3">
                    <span className="text-oxblood/60 text-[10px] tracking-[0.3em] uppercase">Budget</span>
                    <p className="font-display text-oxblood-ink text-lg mt-1">{o.budget || "—"}</p>
                    {o.message ? <p className="text-ink/50 font-light text-sm mt-1 line-clamp-2">{o.message}</p> : null}
                  </div>
                  <div className="md:col-span-3 flex md:flex-col gap-3 md:items-end justify-between">
                    <span className="text-ink/40 text-xs font-light">
                      {o.created_at ? new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                    </span>
                    <select
                      data-testid={`order-status-${i}`}
                      value={o.status || "New"}
                      onChange={(e) => setStatus(o.id, e.target.value)}
                      className="bg-ivory border border-oxblood/30 text-oxblood-ink text-xs tracking-[0.15em] uppercase px-4 py-2.5 cursor-pointer"
                    >
                      {STATUSES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </Reveal>
            ))
          )}
        </div>
      </div>
    </main>
  );
}
